/**
 * The pay-as-you-go floor: from PAYG_FLOOR_EFFECTIVE_DATE every job on a `payg` account deducts at
 * least 40 minutes (PAYG_FLOOR_MINUTES), except while balance the customer PAID for before that date
 * (legacy_paid_seconds) remains: every job is then charged by the minute, the job that uses up the last
 * of it included, and the floor applies from the next job. Enforced in the database
 * (migration 031: lp_charge_once and lp_deduct_balance); this module holds the constants, the
 * customer copy and a pure model of the deduction that the tests pin against the SQL.
 */
import { PAYG_RATE_PER_HOUR_ZAR } from './paygPacks.js';

export const PAYG_FLOOR_SECONDS = 2400;

/** The floor in whole minutes. Every piece of customer copy reads from this, never a typed number. */
export const PAYG_FLOOR_MINUTES = PAYG_FLOOR_SECONDS / 60;

/** Date (YYYY-MM-DD, SAST) from which the floor is charged. */
export const PAYG_FLOOR_EFFECTIVE_DATE = '2026-12-01';

/** The floor in rand, VAT inclusive: 40 minutes at the hourly rate (R5.00). */
export const PAYG_FLOOR_RAND = PAYG_FLOOR_MINUTES / 60 * PAYG_RATE_PER_HOUR_ZAR;

/**
 * What one charge covers, in the one wording every price note, upload line, Help, Terms and banner
 * uses. A language pick of several languages in the modal is ONE charge (one lp_charge_once call);
 * a re-run is charged again once the 24-hour same-file, same-language window has passed or the
 * language differs.
 */
export const PAYG_FLOOR_SCOPE_TEXT = 'for each file and transcription run (a re-run in another language is charged again)';

const rand = (n: number, decimals = 2) => `R${n.toFixed(decimals)}`;

/** "R7.50" */
export const PAYG_RATE_TEXT = rand(PAYG_RATE_PER_HOUR_ZAR);
/** "R5.00" */
export const PAYG_FLOOR_RAND_TEXT = rand(PAYG_FLOOR_RAND);
/** "R0.125" */
export const PAYG_RATE_PER_MINUTE_TEXT = rand(PAYG_RATE_PER_HOUR_ZAR / 60, 3);

/** The Help entry the banner points to (anchor id on the Help page). */
export const PAYG_FLOOR_HELP_ANCHOR = 'pay-as-you-go-minimum';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-12-01" -> "1 December 2026"; anything unparseable -> null. */
export function formatFloorDate(iso: string | null): string | null {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : null;
}

/** "1 December 2026" */
export const PAYG_FLOOR_DATE_TEXT = formatFloorDate(PAYG_FLOOR_EFFECTIVE_DATE) as string;

/** Today's calendar date in SAST (UTC+2, no daylight saving) as YYYY-MM-DD. */
function sastDate(now: Date): string {
  return new Date(now.getTime() + 2 * 3600_000).toISOString().slice(0, 10);
}

/**
 * True once the floor is charged: today (SAST) is on or after the effective date.
 * Copy must never claim the floor is in force before this returns true.
 */
export function paygFloorInForce(now: Date = new Date(), effectiveDate: string = PAYG_FLOOR_EFFECTIVE_DATE): boolean {
  return sastDate(now) >= effectiveDate;
}

/** The instant the floor starts: 00:00 SAST (UTC+2) on PAYG_FLOOR_EFFECTIVE_DATE, as in migration 031. */
function floorInstant(effectiveDate: string): number {
  return Date.parse(`${effectiveDate}T00:00:00+02:00`);
}

/**
 * Seconds of a redeemed gift voucher that count as balance the customer paid for before the floor
 * (legacy_paid_seconds): all of them when the voucher was bought (lp_coupons.created_at) before the
 * effective instant, none otherwise. Mirrors the legacy increment in lp_redeem_coupon (migration 031).
 */
export function giftVoucherLegacySeconds(voucherBoughtAt: Date, seconds: number, effectiveDate: string = PAYG_FLOOR_EFFECTIVE_DATE): number {
  return voucherBoughtAt.getTime() < floorInstant(effectiveDate) ? seconds : 0;
}

/**
 * The sentence shown next to every pay-as-you-go price. Before the effective date it opens with
 * "From 1 December 2026: "; from the date onward that lead is dropped and "Minimum" is capitalised.
 */
export function paygFloorPriceNote(now: Date = new Date()): string {
  const rule = `minimum charge of ${PAYG_FLOOR_MINUTES} minutes of balance (${PAYG_FLOOR_RAND_TEXT}, VAT incl.) ${PAYG_FLOOR_SCOPE_TEXT}.`;
  return paygFloorInForce(now) ? `M${rule.slice(1)}` : `From ${PAYG_FLOOR_DATE_TEXT}: ${rule}`;
}

/**
 * Help page bullets (the Pro sentence is deliberately absent). The two rules that only exist from the
 * effective date open with "From 1 December 2026, " before it; from the date onward that lead is dropped
 * and the next word is capitalised, as in paygFloorPriceNote.
 */
export function paygFloorHelpBullets(now: Date = new Date()): readonly string[] {
  const dated = (rule: string) => (paygFloorInForce(now) ? `${rule[0].toUpperCase()}${rule.slice(1)}` : `From ${PAYG_FLOOR_DATE_TEXT}, ${rule}`);
  return [
    `From ${PAYG_FLOOR_DATE_TEXT}, a job is charged at least ${PAYG_FLOOR_MINUTES} minutes of balance (${PAYG_FLOOR_RAND_TEXT}) ${PAYG_FLOOR_SCOPE_TEXT}.`,
    `Balance you paid for before ${PAYG_FLOOR_DATE_TEXT}, including minutes from a gift voucher bought before that date, is still charged by the minute until it is used up.`,
    `If you have never bought balance or redeemed a gift voucher, your 30 free minutes are charged by the minute, with no minimum. ${dated(`once you buy balance or redeem a gift voucher, any free minutes you still have join your balance and the ${PAYG_FLOOR_MINUTES}-minute minimum applies to them, as it does to promotional minutes.`)}`,
    dated(`once balance you paid for before ${PAYG_FLOOR_DATE_TEXT} is used up, a balance below ${PAYG_FLOOR_MINUTES} minutes is used up by the next job.`),
    'A job that fails is not charged.',
  ];
}

/** The pay-as-you-go line in the Terms. */
export const PAYG_TERMS_LINE = `${PAYG_RATE_TEXT} per hour, VAT inclusive (${PAYG_RATE_PER_MINUTE_TEXT} per minute). From ${PAYG_FLOOR_DATE_TEXT} a job is charged at least ${PAYG_FLOOR_MINUTES} minutes of balance ${PAYG_FLOOR_SCOPE_TEXT}; balance you paid for before that date, including gift vouchers bought before it, is charged by the minute. A failed job is not charged. Balance is deducted after each transcription job. Unused balance does not expire.`;

/** Sign-in banner for pay-as-you-go accounts; stays until the customer acknowledges it. */
export const PAYG_FLOOR_BANNER_TEXT = `From ${PAYG_FLOOR_DATE_TEXT}, pay-as-you-go jobs use at least ${PAYG_FLOOR_MINUTES} minutes of balance (${PAYG_FLOOR_RAND_TEXT}) ${PAYG_FLOOR_SCOPE_TEXT}. Balance you paid for before then, including gift vouchers bought before then, is still charged by the minute; free and promotional minutes are charged the new way. Details in Help.`;

/** Small note in the "Buy a Gift Card" panel of the checkout; date-aware like paygFloorPriceNote. */
export function paygGiftCardNote(now: Date = new Date()): string {
  return paygFloorInForce(now)
    ? `The recipient's jobs use at least ${PAYG_FLOOR_MINUTES} minutes of balance ${PAYG_FLOOR_SCOPE_TEXT}.`
    : `From ${PAYG_FLOOR_DATE_TEXT}, the recipient's jobs use at least ${PAYG_FLOOR_MINUTES} minutes of balance ${PAYG_FLOOR_SCOPE_TEXT}; minutes from a gift voucher bought before ${PAYG_FLOOR_DATE_TEXT} are charged by the minute.`;
}

/** Button on the banner. */
export const PAYG_FLOOR_ACK_LABEL = 'I understand';

/** localStorage key: fallback cache of the acknowledgement (the server record is the source of truth). */
export const paygFloorBannerKey = (userId: string) => `lp:payg-floor-notice-ack:${userId}`;

/** Label of the confirm button shown before a job the floor applies to. */
export const PAYG_FLOOR_CONFIRM_LABEL = `Use ${PAYG_FLOOR_MINUTES} minutes and start`;

/** Shown before the effective date, instead of any floor line, for a pay-as-you-go file shorter than the floor. */
export const PAYG_FLOOR_ADVANCE_LINE = `From ${PAYG_FLOOR_DATE_TEXT}, once balance you paid for before then is used up, files shorter than ${PAYG_FLOOR_MINUTES} minutes use ${PAYG_FLOOR_MINUTES} minutes of balance.`;

/** Shown from the effective date when the file's length is not known (URL input). */
export const PAYG_FLOOR_RULE_LINE = `Each file and transcription run is charged at least ${PAYG_FLOOR_MINUTES} minutes of balance once balance you paid for before ${PAYG_FLOOR_DATE_TEXT} is used up.`;

const isLength = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const isAmount = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;

export interface JobCharge {
  /** Seconds taken off the balance. */
  deducted: number;
  balance: number;
  legacy: number;
  /** True when the floor decided the charge (the job was not charged by the minute against legacy). */
  floored: boolean;
}

/**
 * One job on the personal balance, mirroring the SQL in migration 031 (a = CEIL(seconds)):
 *   pro                      -> nothing
 *   payg, legacy > 0         -> a off the balance and off legacy (per minute, no floor), also for the
 *                               job that uses up the last of legacy (30 legacy min + 35 min file -> 35)
 *   payg, legacy = 0         -> GREATEST(a, 2400) off the balance
 *   anything else            -> a
 * The balance never goes below 0 and legacy never goes above the new balance.
 * `now` is a client-side concern only: the database applies from the day the migration runs,
 * so before the effective date the model charges by the minute.
 */
export function chargeJob(
  plan: string | null | undefined,
  seconds: number,
  balance: number,
  legacy = 0,
  now?: Date,
): JobCharge {
  if (plan === 'pro') return { deducted: 0, balance, legacy, floored: false };
  const a = Math.ceil(seconds);
  if (plan !== 'payg') {
    const deducted = Math.min(balance, a);
    return { deducted, balance: balance - deducted, legacy, floored: false };
  }
  const floorOn = now === undefined || paygFloorInForce(now);
  const covered = !floorOn || legacy > 0;
  const charge = covered ? a : Math.max(a, PAYG_FLOOR_SECONDS);
  const deducted = Math.min(balance, charge);
  const newBalance = balance - deducted;
  const newLegacy = Math.min(Math.max(0, legacy - a), newBalance);
  return { deducted, balance: newBalance, legacy: newLegacy, floored: !covered && charge > a };
}

/** Seconds actually taken off the personal balance for one job (see chargeJob). */
export function deductSeconds(
  plan: string | null | undefined,
  seconds: number,
  balance: number,
  legacy = 0,
  now?: Date,
): number {
  return chargeJob(plan, seconds, balance, legacy, now).deducted;
}

export interface PaygFloorUploadInput {
  plan: string | null | undefined;
  /** Length of the chosen file in seconds; null when unknown (URL input, or not read yet). */
  durationSeconds: number | null | undefined;
  balanceSeconds: number | null | undefined;
  /** legacy_paid_seconds; undefined (column not there yet) counts as 0. */
  legacySeconds?: number | null;
  now?: Date;
}

export interface PaygFloorUpload {
  /** The floor (or advance-notice) sentence, or null when nothing needs saying. */
  message: string | null;
  /** Present only when the balance is below the floor and the floor applies. */
  lowBalance: string | null;
  /** True when the customer must press the confirm button before the job starts. */
  requiresConfirm: boolean;
}

const NOTHING: PaygFloorUpload = { message: null, lowBalance: null, requiresConfirm: false };

/**
 * The upload-time disclosure and confirm decision, as a pure function (the components and the
 * start button both read it). Only a `payg` account sees anything.
 *  - before the effective date: the advance notice for a short or unknown file, never a confirm;
 *  - from the date: a file shorter than the floor on an account with no legacy balance gets the exact
 *    charge and requires confirmation; an unknown length (URL) gets the rule line and requires
 *    confirmation unless the account still has legacy balance (then the job is charged by the minute).
 */
export function paygFloorUpload(input: PaygFloorUploadInput): PaygFloorUpload {
  if (input.plan !== 'payg') return NOTHING;
  const now = input.now ?? new Date();
  const known = isLength(input.durationSeconds);
  const legacy = isAmount(input.legacySeconds) ? input.legacySeconds : 0;
  const balance = isAmount(input.balanceSeconds) ? input.balanceSeconds : null;

  if (!paygFloorInForce(now)) {
    if (known && Math.ceil(input.durationSeconds as number) >= PAYG_FLOOR_SECONDS) return NOTHING;
    if (known && legacy > 0) return NOTHING;
    return { message: PAYG_FLOOR_ADVANCE_LINE, lowBalance: null, requiresConfirm: false };
  }

  if (!known) {
    // Length unknown: it may be short. Any legacy balance means the job is charged by the minute
    // (the job that uses it up included); only with none left may the floor apply, so ask.
    if (legacy > 0) return NOTHING;
    return { message: PAYG_FLOOR_RULE_LINE, lowBalance: null, requiresConfirm: true };
  }

  const seconds = input.durationSeconds as number;
  const job = chargeJob('payg', seconds, balance ?? Number.POSITIVE_INFINITY, legacy, now);
  if (!job.floored) return NOTHING;

  const n = Math.ceil(seconds / 60);
  const message = `This ${n}-minute file will use ${PAYG_FLOOR_MINUTES} minutes of balance, because each file and transcription run is charged at least ${PAYG_FLOOR_MINUTES} minutes.`;
  const lowBalance = balance !== null && balance < PAYG_FLOOR_SECONDS
    ? `Your balance is ${Math.floor(balance / 60)} minutes. This job will use all of it.`
    : null;
  return { message, lowBalance, requiresConfirm: true };
}

/**
 * Duration to feed the selector: only a chosen local file has a known length. On the URL tab a
 * leftover local `file` (its length) must not describe the URL job.
 */
export function floorDurationFor(inputType: 'local' | 'url' | string, fileDurationS: number | null | undefined): number | null {
  return inputType === 'local' && isLength(fileDurationS) ? fileDurationS : null;
}
